/**
 * PLAT-376 (Shell 2b): Workspace's one wrapper over Foundation's AppShell.
 * `lane` picks the navigation (`shellNav.ts`). AdminShell mounts the admin lane
 * (Shell 2c, PLAT-377); UserShell and signed-in /legal mount the user lane
 * (Shell 3, PLAT-365), which replaced TopNav.
 *
 * The top bar keeps everything TopNav's right-hand cluster gave both modes,
 * with the same targets and accessible names where the markup is ours: the
 * search control (Shell 4: it opens SearchPalette, Foundation's CommandPalette,
 * as do ⌘K / Ctrl+K and the chat rail's search sliver), the workspace
 * name, NotificationBell, Help, the gear flip (the one sanctioned cross-lane
 * jump: "Back to App" in the admin lane, the admin gear for admin and owner in
 * the user lane) and the account menu. In the admin lane the shell's search
 * field is the old admin sidebar's nav search: it lists the matching entries in
 * the rail. The user lane has none, as TopNav had none.
 *
 * Below md, where the rail becomes a drawer, the bar's actions can't shrink, so
 * the workspace name drops out (TopNav hid its pill on phones too) and the
 * search chip becomes an icon. Notifications, Help, the gear and the account
 * menu then fit at 375px.
 *
 * The admin lane's bar also holds the nav search field, which Foundation never
 * shrinks below its input's width (PLAT-387). So the admin lane is compact
 * below lg too, where the expanded rail leaves the bar about 500px at 768px,
 * and below md it drops the field: the drawer lists every admin entry, and the
 * search chip opens the same admin palette. From lg up the bar is unchanged.
 */

import {
  Badge,
  Button,
  AppShell as FoundationAppShell,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  PageContent,
  PoweredByOlytic,
  TooltipProvider,
  UserMenu,
  useBreakpoint,
  useCommandPalette,
  useIsMobile,
} from '@olytic/foundation';
import { type ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { setShellScroller } from '../hooks/useScrollToTop.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useSignOut } from '../hooks/useSignOut.ts';
import { fetchAdminOverview } from '../lib/api.ts';
import { formatLabel } from '../lib/format.ts';
import { RouterNavLink } from '../lib/foundationLinks.tsx';
import type { CurrentUser } from '../lib/types.ts';
import { IconGear, IconGrid, IconHelp, IconSearch } from './Icon.tsx';
import { NotificationBell } from './NotificationBell.tsx';
import { ObjectIcon, type ObjectKind } from './ObjectIcon.tsx';
import { type RequestDestination, RequestModal } from './RequestModal.tsx';
import { SearchPalette } from './SearchPalette.tsx';
import {
  ADMIN_NAV,
  ADMIN_NAV_SEARCH,
  activeNavKey,
  hitNodes,
  navFor,
  openNavGroups,
  searchNav,
  toNavNodes,
  USER_NAV,
} from './shellNav.ts';

export type ShellLane = 'admin' | 'user';

interface AppShellProps {
  lane: ShellLane;
  /** Full-width strip above the rail and content: the session banners. */
  banner?: ReactNode;
  children: ReactNode;
}

/**
 * A nav entry's glyph is decorative: Foundation renders it beside the visible
 * label (or names the collapsed rail's icon with aria-label), so its
 * role="img" name is hidden here. Otherwise "Home" reads as "Home Home".
 */
const navIcon = (kind: ObjectKind) => (
  <span aria-hidden="true" style={{ display: 'inline-flex' }}>
    <ObjectIcon kind={kind} size="xs" variant="glyph" />
  </span>
);

/**
 * The workspace logo, else Olytic's mark; a logo that fails to load falls back
 * to the mark.
 */
function BrandLogo({ user }: { user: CurrentUser }) {
  const custom = Boolean(user.account_id && user.brand_logo_r2_key);
  const src = custom
    ? `/workspace-logo/${user.account_id}?v=${encodeURIComponent(user.brand_logo_updated_at ?? '1')}`
    : '/olytic-mark.png';
  return (
    <img
      src={src}
      alt={custom && user.account_name ? `${user.account_name} logo` : ''}
      aria-hidden={!custom}
      width={28}
      height={28}
      onError={(e) => {
        if (e.currentTarget.src.includes('/workspace-logo/'))
          e.currentTarget.src = '/olytic-mark.png';
      }}
    />
  );
}

/** The rail's brand: the workspace logo when one is uploaded, else Olytic's mark. */
function Brand({
  user,
  home,
  compact = false,
}: {
  user: CurrentUser;
  home: string;
  compact?: boolean;
}) {
  const custom = Boolean(user.brand_logo_r2_key);
  return (
    <Link
      to={home}
      className="flex min-w-0 items-center gap-2.5 no-underline"
      aria-label={custom ? user.account_name : 'Olytic'}
    >
      <BrandLogo user={user} />
      {compact ? null : (
        <span className="truncate text-sm font-strong tracking-tight text-[var(--nav-fg-strong)]">
          {custom && user.account_name ? user.account_name : 'Olytic'}
        </span>
      )}
    </Link>
  );
}

export function AppShell({ lane, banner, children }: AppShellProps) {
  const { user } = useSession();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const handleSignOut = useSignOut();
  // Foundation's breakpoint: true below md, where the shell's nav is a drawer.
  const mobile = useIsMobile();
  const wide = useBreakpoint('lg');
  const [query, setQuery] = useState('');
  // ⌘K / Ctrl+K toggles it (PLAT-181 criterion 4).
  const { open: searchOpen, setOpen: setSearchOpen } = useCommandPalette();
  const [request, setRequest] = useState<{ open: boolean; destination: RequestDestination }>({
    open: false,
    destination: 'olytic',
  });
  const [count, setCount] = useState<number | undefined>(undefined);
  const role = user?.role ?? 'user';
  const artifacts = user?.artifacts_enabled === true;
  const tree = useMemo(
    () => (lane === 'admin' ? navFor(ADMIN_NAV, role) : navFor(USER_NAV, role, artifacts)),
    [lane, role, artifacts],
  );
  // Groups open on first render, so a deep link lands with its group open.
  const [openGroups] = useState(() => openNavGroups(tree, pathname));

  // Foundation's shell scrolls its <main>, not the window. Registering it lets
  // App's ScrollToTop start a new page at the top, and the reauth stash and
  // restore (AdminShell, useScrollToTop) keep the offset across sign-in.
  const content = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const main = content.current?.closest('main') as HTMLElement | null | undefined;
    if (!main) return;
    setShellScroller(main);
    return () => setShellScroller(null);
  }, []);

  // The collapsed chat history rail's "Search chats" sliver opens the palette
  // through this window event (Chat.tsx), without expanding the rail.
  useEffect(() => {
    const open = () => setSearchOpen(true);
    window.addEventListener('olytic:global-search:open', open);
    return () => window.removeEventListener('olytic:global-search:open', open);
  }, [setSearchOpen]);

  // The admin lane's People count: pending invitations plus open requests. The rail works without it.
  useEffect(() => {
    if (lane !== 'admin') return;
    let cancelled = false;
    fetchAdminOverview()
      .then((d) => {
        if (!cancelled) setCount(d.counts.pending_invitations + d.counts.open_requests);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [lane]);

  if (user === null) return null;
  const admin = lane === 'admin';
  // The admin lane's nav search field, kept out of the 375px bar (see the top).
  const navSearch = admin && !mobile;
  const compact = mobile || (admin && !wide);
  const searching = navSearch && query.trim() !== '';
  const items = searching
    ? hitNodes(searchNav(ADMIN_NAV_SEARCH, role, query))
    : toNavNodes(tree, navIcon, count);
  const home = admin ? '/admin' : '/home';
  const canAccessAdmin = role === 'owner' || role === 'admin';
  const avatarKey = user.avatar_r2_object_key;

  return (
    <TooltipProvider>
      <FoundationAppShell
        brand={<Brand user={user} home={home} />}
        brandMark={<Brand user={user} home={home} compact />}
        items={items}
        activeKey={activeNavKey(tree, pathname) ?? ''}
        defaultOpenGroups={openGroups}
        linkComponent={RouterNavLink}
        {...(navSearch ? { onSearch: setQuery } : {})}
        // A hit closes the search, as the old sidebar's did.
        onNavigate={() => setQuery('')}
        pinnedItems={
          searching
            ? [{ key: 'clear-search', label: 'Clear search', onSelect: () => setQuery('') }]
            : []
        }
        banner={banner}
        // The user lane keeps UserShell's corner attribution instead.
        sidebarFooter={admin && user.brand_logo_r2_key ? <PoweredByOlytic /> : null}
        topBarActions={
          <>
            <Button
              variant="outline"
              size={compact ? 'icon' : 'sm'}
              onClick={() => setSearchOpen(true)}
              title="Search"
              aria-label="Open command palette"
            >
              <IconSearch size={14} />
              {compact ? null : 'Search…'}
            </Button>
            {compact ? null : (
              <Badge tone="neutral" title={`${user.account_name} workspace`}>
                <span>Workspace</span> <span>{user.account_name}</span>
              </Badge>
            )}
            <NotificationBell />
            <Menu>
              <MenuTrigger asChild>
                <Button
                  variant="subtle"
                  size="icon"
                  aria-label="Help and feedback"
                  title="Help and feedback"
                >
                  <IconHelp size={20} />
                </Button>
              </MenuTrigger>
              <MenuContent align="end">
                <MenuItem onSelect={() => setRequest({ open: true, destination: 'admin' })}>
                  Contact Your Admin
                </MenuItem>
                <MenuItem onSelect={() => setRequest({ open: true, destination: 'olytic' })}>
                  Contact Olytic
                </MenuItem>
              </MenuContent>
            </Menu>
            {admin ? (
              <Button asChild variant="subtle" size="icon">
                <Link to="/home" title="Back to app" aria-label="Back to user mode">
                  <IconGrid size={20} />
                </Link>
              </Button>
            ) : canAccessAdmin ? (
              <Button asChild variant="subtle" size="icon">
                <Link to="/admin" title="Admin settings" aria-label="Switch to admin mode">
                  <IconGear size={20} />
                </Link>
              </Button>
            ) : null}
            <UserMenu
              name={user.name}
              email={user.email}
              role={formatLabel(user.role)}
              {...(avatarKey
                ? {
                    avatarUrl: `/avatar/${user.user_id}?v=${encodeURIComponent(user.avatar_updated_at ?? '1')}`,
                  }
                : {})}
              actions={[
                { key: 'settings', label: 'Settings', onSelect: () => navigate('/settings') },
                { key: 'activity', label: 'My activity', onSelect: () => navigate('/activity') },
                { key: 'legal', label: 'Legal & policies', onSelect: () => navigate('/legal') },
              ]}
              onSignOut={() => void handleSignOut()}
            />
          </>
        }
      >
        <PageContent width="wide">
          <div ref={content}>{children}</div>
        </PageContent>
      </FoundationAppShell>
      <RequestModal
        open={request.open}
        onClose={() => setRequest((s) => ({ ...s, open: false }))}
        destination={request.destination}
      />
      <SearchPalette open={searchOpen} onOpenChange={setSearchOpen} scope={lane} />
    </TooltipProvider>
  );
}
